import {useContext} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {atomPanelElevation, atomPanelRounded} from '@s-ui/react-atom-panel'
import Context from '@s-ui/react-context'
import {useDateFormat} from '@adv-mt/mt-hook-use-date-format'
import MediaVehicleRegistrationPlate from '@adv-mt/mt-media-vehicle-registration-plate'
import {PanelBasic} from '@adv-mt/mt-panel-basic'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

const BASE_CLASS = 'mt-PanelLastRegistrationPlate'
const LAST_PLATE_CLASS = `${BASE_CLASS}-lastPlate`
const LAST_PLATE_DESCRIPTION_CLASS = `${LAST_PLATE_CLASS}Description`
const LAST_PLATE_WRAPPER_CLASS = `${LAST_PLATE_CLASS}Wrapper`
const CTA_DESCRIPTION_CLASS = `${LAST_PLATE_CLASS}CtaDescription`
const CTA_CLASS = `${LAST_PLATE_CLASS}Cta`
const OTHER_PLATES_CLASS = `${BASE_CLASS}-otherPlates`
const OTHER_PLATES_GRID_CLASS = `${OTHER_PLATES_CLASS}Grid`
const OTHER_PLATES_GRID_ITEM_CLASS = `${OTHER_PLATES_GRID_CLASS}Item`
const OTHER_PLATES_GRID_ITEM_DATE_CLASS = `${OTHER_PLATES_GRID_ITEM_CLASS}Date`

const linkFactoryDefaultProp = ({children, ...rest}) => <a {...rest}>{children}</a>

const PanelLastRegistrationPlate = ({
  registrationPlates = [],
  numEstimatedVehicles,
  linkFactory = linkFactoryDefaultProp
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')

  const {VEHICLE_CONFIGURATOR_PATH} = config.get('page')

  const [lastPlate = {}] = registrationPlates
  const otherPlates = registrationPlates.slice(1)
  const {descriptiveShort: parsedDate} = useDateFormat(lastPlate.date)

  return (
    <div className={BASE_CLASS}>
      <PanelBasic elevation={atomPanelElevation.NONE} withBorder rounded={atomPanelRounded.L}>
        <div className={LAST_PLATE_CLASS}>
          <TitleBasic
            as={titleBasicTags.h3}
            size={titleBasicSizes.s}
            title={i18n.t('PANEL_LAST_REGISTRATION_PLATE.TITLE')}
          />
          <p className={LAST_PLATE_DESCRIPTION_CLASS}>
            {i18n.t('PANEL_LAST_REGISTRATION_PLATE.DESCRIPTION', {
              date: parsedDate || '',
              numEstimatedVehicles
            })}
          </p>
          <div className={LAST_PLATE_WRAPPER_CLASS}>
            <MediaVehicleRegistrationPlate plateNumber={lastPlate.plateNumber} />
          </div>
          <p className={CTA_DESCRIPTION_CLASS}>{i18n.t('PANEL_LAST_REGISTRATION_PLATE.CTA_DESCRIPTION')}</p>
          <div className={CTA_CLASS}>
            <AtomButton
              design={atomButtonDesigns.SOLID}
              link
              linkFactory={linkFactory}
              size={atomButtonSizes.MEDIUM}
              shape={atomButtonShapes.CIRCULAR}
              href={VEHICLE_CONFIGURATOR_PATH}
            >
              {i18n.t('PANEL_LAST_REGISTRATION_PLATE.CTA_TEXT')}
            </AtomButton>
          </div>
        </div>
      </PanelBasic>
      {otherPlates && otherPlates.length && (
        <div className={OTHER_PLATES_CLASS}>
          <TitleBasic
            as={titleBasicTags.h3}
            size={titleBasicSizes.s}
            title={i18n.t('PANEL_LAST_REGISTRATION_PLATE.OTHER_PLATES_TITLE')}
          />
          <ul className={OTHER_PLATES_GRID_CLASS}>
            {otherPlates.map((plate, index) => {
              const {plateNumber, date} = plate
              return (
                <li key={`plate-${index}`} className={OTHER_PLATES_GRID_ITEM_CLASS}>
                  <p className={OTHER_PLATES_GRID_ITEM_DATE_CLASS}>{date}</p>
                  <MediaVehicleRegistrationPlate plateNumber={plateNumber} />
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

PanelLastRegistrationPlate.displayName = 'PanelLastRegistrationPlate'
PanelLastRegistrationPlate.propTypes = {
  registrationPlates: PropTypes.arrayOf(
    PropTypes.shape({
      plateNumber: PropTypes.string,
      date: PropTypes.string
    })
  ),
  linkFactory: PropTypes.func,
  numEstimatedVehicles: PropTypes.string
}

export default PanelLastRegistrationPlate
